/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.19.0
 */

import { AnyAction, createListenerMiddleware, ThunkDispatch } from '@reduxjs/toolkit';
import { LOCATION_CHANGE } from 'connected-react-router';
import get from 'lodash/get';

import { checkESTableList } from '@utils/checkESTable';

import { logBusinessEvent } from '../../logging';
import { initialise, reset, setTableLocationPathname, tableSlice } from '../slice';
import { tableApi } from '../store';
import type { ITableInitialise } from '../types';
import { getTableLoadSize } from '../utils/getTableLoadSize';

export const tableListenerMiddleware = createListenerMiddleware();

tableListenerMiddleware.startListening({
    actionCreator: initialise,
    effect: async (action, { dispatch, getState }) => {
        /* Инициализация таблицы*/
        if (action.type === initialise.type) {
            /* Сохранение текущего роутинга таблица */
            const pathname = get(getState(), 'router.location.pathname');
            const isTableListLoading = get(getState(), 'tableSlice.requestStatus.list.isLoading');
            const isFastBasketPage = get(getState(), 'tableSlice.isFastBasketPage');
            
            // Контекст нужен если таблица вызывается не из основного места
            // Например из форм основных операций
            const context = get(getState(), 'tableSlice.context');
            if (pathname) {
                dispatch(setTableLocationPathname(pathname));
            }

            if (action.payload.preset && action.payload.filterAndSort) {
                const { filter, sort, filterId } = action.payload.preset;
                /* Предустановленная фильтрация и сортировка */
                if (filter || sort) {
                    await dispatch(tableApi.endpoints.setTableFilterAndSort.initiate({
                        url: action.payload.filterAndSort.url,
                        filter,
                        sort,
                    }));
                }
                /* Фильтр-preset */
                if (filterId && action.payload.filterAndSort.preset) {
                    await dispatch(tableApi.endpoints.setTableFilterAndSortPreset.initiate({
                        url: action.payload.filterAndSort.preset,
                        filterId,
                    }));
                }
            }

            /* Загрузка меток */
            if (action.payload.labels?.url) {
                dispatch(
                    tableApi.endpoints.getLabelsList.initiate({ url: action.payload.labels.url }, {
                        forceRefetch: true,
                    }),
                );
            }

            /* Загрузка meta таблицы */
            if (action.payload.meta) {
                dispatch(
                    tableApi.endpoints.getTableMeta.initiate(
                        { 
                            url: action.payload.meta.url,
                            context,
                        }, { forceRefetch: true }),
                ).then(() => {
                    const { list, basket } = action.payload;

                    /* Загрузка списка */
                    /* Не загружаем список если он уже загружается */
                    if (list && !isTableListLoading) {
                        loadTableList(dispatch, list, context);
                    }

                    /* Загрузка корзины */
                    /* Не загружаем корзину, если мы на странице, где корзина не нужна */
                    if (basket && !isFastBasketPage) {
                        dispatch(
                            tableApi.endpoints.getBasketList.initiate({ url: basket.list, from: 0, size: 24 }, {
                                forceRefetch: true,
                            }),
                        );
                    }
                });
            }
            /* Для простых таблиц без меты запрашиваются только данные */
            else if (action.payload.list) {
                loadTableList(dispatch, action.payload.list, context);
            }
        }
    },
});

const loadTableList = (
    dispatch: ThunkDispatch<unknown, unknown, AnyAction>, 
    list: Required<ITableInitialise>['list'],
    context?: string,
) => {
    const size = getTableLoadSize(list);

    // параметр use_search_after требуется для списков, получаемых из Elastic Search (TableSubscriber)
    dispatch(
        tableApi.endpoints.getTableList.initiate({
            url: list.url,
            from: 0,
            size,
            use_search_after: checkESTableList(list.url),
            context,
        }, { forceRefetch: true }),
    );
    /* Логирование инициализации списка (опционально) */
    if (list.log) {
        dispatch(logBusinessEvent(list.log));
    }
};

tableListenerMiddleware.startListening({
    type: LOCATION_CHANGE,
    effect: (action, { dispatch, getState }) => {
        if (action.type === LOCATION_CHANGE) {
            const pathname = get(getState(), 'router.location.pathname');
            const savePathname = get(getState(), `${tableSlice.name}.tableLocation`);
            // Если перешли на др страницу, то необходимо очистить стор последней таблицы
            if (pathname !== savePathname) {
                dispatch(tableApi.util.resetApiState());
                dispatch(reset());
            }
        }
    },
});
